import React, { useState } from 'react';
import { FileText, Calendar, Tag, Maximize2, Image as ImageIcon, BookOpen } from 'lucide-react';
import { Link } from 'react-router-dom';
import ScrollReveal from '../components/ScrollReveal';
import PdfViewer from '../components/PdfViewer';
import Lightbox from '../components/Lightbox';
import { newsData } from '../projectsData';
import { articlesData } from '../data/articlesData';
import './News.css';
import SEO, { seoConfig } from '../components/SEO';

export default function News({ t, language }) {
  const isEN = language === 'en';
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
  const [currentPdf, setCurrentPdf] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImage, setCurrentImage] = useState(null);

  // Fonction pour déterminer si c'est un PDF ou une image
  const isPdf = (url) => {
    return url && url.toLowerCase().endsWith('.pdf');
  };

  // Fonction pour déterminer si c'est un article texte
  const isArticle = (item) => {
    return !!item.articleSlug;
  };

  const openPdf = (pdf, title) => {
    setCurrentPdf({ url: pdf, title });
    setPdfViewerOpen(true);
  };

  const closePdf = () => {
    setPdfViewerOpen(false);
    setCurrentPdf(null);
  };

  const openImage = (image, title) => {
    setCurrentImage({ src: image, title });
    setLightboxOpen(true);
  };

  const closeImage = () => {
    setLightboxOpen(false);
    setCurrentImage(null);
  };

  const openMedia = (item) => {
    const newsItem = item.text || {};
    if (isPdf(item.pdf)) {
      openPdf(item.pdf, newsItem.title);
    } else {
      openImage(item.pdf, newsItem.title);
    }
  };

  // Récupère les données de l'article depuis articlesData
  const getArticleData = (slug) => {
    return articlesData.find(a => a.slug === slug);
  };

  // Textes d'une entrée : pour un article, articlesData fait foi (source unique, déjà
  // bilingue) ; pour une parution presse, on garde t.newsData (aligné par index sur newsData).
  // L'association est faite AVANT le tri, puis la liste est triée du plus récent au plus ancien.
  const orderedNews = newsData
    .map((item, idx) => {
      const article = item.articleSlug ? getArticleData(item.articleSlug) : null;
      const text = article
        ? {
            date: isEN ? (article.dateEN || article.date) : article.date,
            category: isEN ? (article.categoryEN || article.category) : article.category,
            title: isEN ? (article.titleEN || article.title) : article.title,
            excerpt: isEN ? (article.excerptEN || article.excerpt) : article.excerpt
          }
        : (t.newsData?.[idx] || {});
      return {
        ...item,
        text,
        sortDate: article?.dateISO || item.dateISO || ''
      };
    })
    .sort((a, b) => String(b.sortDate).localeCompare(String(a.sortDate)));

  return (
    <div className="news-page">
      {/* SEO Component */}
      <SEO
        title={seoConfig.news.title}
        description={seoConfig.news.description}
        keywords={seoConfig.news.keywords}
      />

      <div className="page-container">
        <ScrollReveal>
          <h1 className="page-title page-mb">{t.news.title}</h1>
          <p className="page-subtitle">{t.news.subtitle}</p>
        </ScrollReveal>

        {/* Message si aucune actualité */}
        {orderedNews.length === 0 ? (
          <ScrollReveal delay={0.1}>
            <div className="news-empty">
              <FileText size={64} strokeWidth={1} />
              <p>{t.news.noNews}</p>
            </div>
          </ScrollReveal>
        ) : (
          <div className="news-grid">
            {orderedNews.map((item, idx) => {
              const newsItem = item.text; // textes traduits, associés avant le tri

              // Cas article texte (nouveau type)
              if (isArticle(item)) {
                const articleData = getArticleData(item.articleSlug);
                return (
                  <ScrollReveal key={item.id} delay={idx * 0.1}>
                    <article className="news-card news-card--article">
                      {/* Image de couverture de l'article */}
                      <div className="news-media-preview">
                        {articleData && (
                          <img
                            src={articleData.image}
                            alt={newsItem.title}
                            className="news-image"
                            loading="lazy"
                          />
                        )}
                        <div className="news-article-badge">
                          <BookOpen size={14} />
                          Article
                        </div>
                      </div>

                      {/* Contenu */}
                      <div className="news-content">
                        <div className="news-meta">
                          <span className="news-date">
                            <Calendar size={14} />
                            {newsItem.date}
                          </span>
                          <span className="news-category">
                            <Tag size={14} />
                            {newsItem.category}
                          </span>
                        </div>

                        <h2 className="news-title">{newsItem.title}</h2>
                        <p className="news-excerpt">{newsItem.excerpt}</p>

                        <Link
                          to={`/actualites/${item.articleSlug}`}
                          className="news-button"
                        >
                          <BookOpen size={18} />
                          Lire l'article
                        </Link>
                      </div>
                    </article>
                  </ScrollReveal>
                );
              }

              // Cas PDF ou Image (comportement original)
              return (
                <ScrollReveal key={item.id} delay={idx * 0.1}>
                  <article className="news-card">
                    {/* Prévisualisation - PDF ou Image (détection automatique) */}
                    <div className="news-media-preview">
                      {isPdf(item.pdf) ? (
                        // Prévisualisation PDF
                        <>
                          <iframe
                            src={`${item.pdf}#toolbar=0&navpanes=0&scrollbar=0`}
                            className="pdf-iframe"
                            title={newsItem.title}
                          />
                          <div
                            className="media-overlay-button"
                            onClick={() => openMedia(item)}
                          >
                            <div className="expand-button">
                              <Maximize2 size={24} />
                              <span>{t.news.expand}</span>
                            </div>
                          </div>
                        </>
                      ) : (
                        // Prévisualisation Image
                        <>
                          <img
                            src={item.pdf}
                            alt={newsItem.title}
                            className="news-image"
                            loading="lazy"
                            decoding="async"
                          />
                          <div
                            className="media-overlay-button"
                            onClick={() => openMedia(item)}
                          >
                            <div className="expand-button">
                              <Maximize2 size={24} />
                              <span>{t.news.expand}</span>
                            </div>
                          </div>
                        </>
                      )}
                    </div>

                    {/* Contenu */}
                    <div className="news-content">
                      {/* Meta informations */}
                      <div className="news-meta">
                        <span className="news-date">
                          <Calendar size={14} />
                          {newsItem.date}
                        </span>
                        <span className="news-category">
                          <Tag size={14} />
                          {newsItem.category}
                        </span>
                      </div>

                      {/* Titre */}
                      <h2 className="news-title">{newsItem.title}</h2>

                      {/* Extrait */}
                      <p className="news-excerpt">{newsItem.excerpt}</p>

                      {/* Bouton adapté au type */}
                      <button
                        onClick={() => openMedia(item)}
                        className="news-button"
                      >
                        {isPdf(item.pdf) ? (
                          <>
                            <FileText size={18} />
                            {t.news.readArticle}
                          </>
                        ) : (
                          <>
                            <ImageIcon size={18} />
                            {t.news.viewImage}
                          </>
                        )}
                      </button>
                    </div>
                  </article>
                </ScrollReveal>
              );
            })}
          </div>
        )}
      </div>

      {/* Viewer PDF plein écran */}
      {pdfViewerOpen && currentPdf && (
        <PdfViewer
          pdfUrl={currentPdf.url}
          title={currentPdf.title}
          onClose={closePdf}
        />
      )}

      {/* Lightbox pour les images */}
      {lightboxOpen && currentImage && (
        <Lightbox
          images={[currentImage.src]}
          currentIndex={0}
          onClose={closeImage}
          onNext={closeImage}
          onPrev={closeImage}
        />
      )}
    </div>
  );
}
